import {
  Table as TableIcon,
  Trash2,
  Eraser,
  FileEdit,
  Download,
  Upload,
  GitCompareArrows,
  Copy,
} from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useDialogStore } from '@/stores/dialog-store';
import { useToastStore } from '@/stores/toast-store';
import { apiClient } from '@/api/client';
import type { Command } from '../types';

interface TableTabLike {
  type: 'table' | 'structure' | 'designer';
  connectionId: string;
  database?: string;
  schema: string;
  table?: string;
  tableName?: string;
  id: string;
}

function currentTable(tab: unknown): TableTabLike | null {
  if (!tab || typeof tab !== 'object') return null;
  const t = tab as { type?: string } & Record<string, unknown>;
  if (t.type === 'table' || t.type === 'structure') return tab as TableTabLike;
  if (t.type === 'designer' && t.tableName) return tab as TableTabLike;
  return null;
}

function tableName(t: TableTabLike): string {
  return (t.table ?? t.tableName) as string;
}

/**
 * Commands that act on the currently focused table. Every guard uses
 * `currentTable()` so table-like tabs (data grid, structure, editing a
 * specific table in the designer) all get the same command set.
 */
export const tableCommands: Command[] = [
  {
    id: 'table.show-data',
    label: 'Show Table Data',
    icon: TableIcon,
    category: 'Table',
    keywords: ['open', 'grid', 'select'],
    when: (ctx) => currentTable(ctx.activeTab) !== null,
    run: (ctx) => {
      const t = currentTable(ctx.activeTab);
      if (!t) return;
      const name = tableName(t);
      useWorkspaceStore.getState().openTab({
        type: 'table',
        id: `table:${t.connectionId}:${t.schema}.${name}`,
        connectionId: t.connectionId,
        database: t.database,
        schema: t.schema,
        table: name,
        where: '',
        orderBy: '',
      });
    },
  },
  {
    id: 'table.show-structure',
    label: 'Show Table Structure',
    icon: FileEdit,
    category: 'Table',
    keywords: ['ddl', 'columns', 'schema'],
    when: (ctx) => currentTable(ctx.activeTab) !== null,
    run: (ctx) => {
      const t = currentTable(ctx.activeTab);
      if (!t) return;
      const name = tableName(t);
      useWorkspaceStore.getState().openTab({
        type: 'structure',
        id: `structure:${t.connectionId}:${t.schema}.${name}`,
        connectionId: t.connectionId,
        database: t.database,
        schema: t.schema,
        table: name,
      });
    },
  },
  {
    id: 'table.open-designer',
    label: 'Open in Table Designer',
    icon: FileEdit,
    category: 'Table',
    keywords: ['alter', 'modify', 'edit'],
    when: (ctx) => currentTable(ctx.activeTab) !== null,
    run: (ctx) => {
      const t = currentTable(ctx.activeTab);
      if (!t) return;
      const name = tableName(t);
      useWorkspaceStore.getState().openTab({
        type: 'designer',
        id: `designer:${t.connectionId}:${t.schema}:${name}`,
        connectionId: t.connectionId,
        database: t.database,
        schema: t.schema,
        tableName: name,
        mode: 'alter',
      });
    },
  },
  {
    id: 'table.export',
    label: 'Export Table...',
    icon: Download,
    category: 'Table',
    keywords: ['download', 'csv', 'json'],
    when: (ctx) => currentTable(ctx.activeTab)?.type === 'table',
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:table:export'));
    },
  },
  {
    id: 'table.import',
    label: 'Import Data...',
    icon: Upload,
    category: 'Table',
    keywords: ['upload', 'csv', 'load'],
    when: (ctx) => currentTable(ctx.activeTab) !== null,
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:table:import'));
    },
  },
  {
    id: 'table.compare',
    label: 'Compare With...',
    icon: GitCompareArrows,
    category: 'Table',
    keywords: ['diff'],
    when: (ctx) => currentTable(ctx.activeTab)?.type === 'table',
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:table:compare'));
    },
  },
  {
    id: 'table.copy-name',
    label: 'Copy Qualified Name',
    icon: Copy,
    category: 'Table',
    keywords: ['clipboard'],
    when: (ctx) => currentTable(ctx.activeTab) !== null,
    run: (ctx) => {
      const t = currentTable(ctx.activeTab);
      if (!t) return;
      const qualified = `${t.schema}.${tableName(t)}`;
      navigator.clipboard.writeText(qualified).catch(() => { /* ignore */ });
      useToastStore.getState().addToast('success', `Copied "${qualified}".`);
    },
  },
  {
    id: 'table.truncate',
    label: 'Truncate Table...',
    icon: Eraser,
    category: 'Table',
    keywords: ['empty', 'clear'],
    when: (ctx) => currentTable(ctx.activeTab) !== null,
    run: (ctx) => {
      const t = currentTable(ctx.activeTab);
      if (!t) return;
      const name = tableName(t);
      useDialogStore.getState().showConfirm({
        title: 'Truncate Table',
        message: `Remove all rows from "${t.schema}.${name}"? Cannot be undone.`,
        confirmText: name,
        danger: true,
        onConfirm: async () => {
          await apiClient.post(`/api/management/${t.connectionId}/truncate-table`, {
            database: t.database,
            schema: t.schema,
            table: name,
          });
          useToastStore.getState().addToast('success', `Truncated "${t.schema}.${name}".`);
        },
      });
    },
  },
  {
    id: 'table.drop',
    label: 'Drop Table...',
    icon: Trash2,
    category: 'Table',
    keywords: ['delete', 'remove'],
    when: (ctx) => currentTable(ctx.activeTab) !== null,
    run: (ctx) => {
      const t = currentTable(ctx.activeTab);
      if (!t) return;
      const name = tableName(t);
      useDialogStore.getState().showConfirm({
        title: 'Drop Table',
        message: `Permanently delete "${t.schema}.${name}" and all its data?`,
        confirmText: name,
        danger: true,
        onConfirm: async () => {
          await apiClient.post(`/api/management/${t.connectionId}/drop-table`, {
            database: t.database,
            schema: t.schema,
            table: name,
          });
          useWorkspaceStore.getState().closeTab(t.id);
          useToastStore.getState().addToast('success', `Dropped "${t.schema}.${name}".`);
        },
      });
    },
  },
];
